import React, { useState } from 'react';
import { Bar, Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { AnalyticsFilters } from '../types/analytics.types';
import { useSkipsAnalytics, SkipsData } from '../hooks/useAnalytics';
import './SkipsTab.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface SkipsTabProps {
  filters: AnalyticsFilters;
}

type ViewMode = 'overview' | 'components' | 'reasons' | 'words' | 'sections' | 'users';

const chartColors = [
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
  '#E7E9ED',
  '#7C4DFF',
  '#00E676',
  '#FF5252',
];

export const SkipsTab: React.FC<SkipsTabProps> = ({ filters }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const { data, loading, error, refetch } = useSkipsAnalytics(filters);

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14 },
        bodyFont: { size: 13 },
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
      },
      x: {
        grid: { display: false },
        ticks: { maxRotation: 45, minRotation: 45 }
      }
    }
  };

  // Time series chart data
  const timeSeriesChartData = data?.time_series ? {
    labels: data.time_series.map(t => t.date),
    datasets: [
      {
        label: 'Skip Events',
        data: data.time_series.map(t => t.skip_count),
        borderColor: '#FF6384',
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#FF6384',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
      {
        label: 'Unique Users',
        data: data.time_series.map(t => t.unique_users),
        borderColor: '#36A2EB',
        backgroundColor: 'rgba(54, 162, 235, 0.1)',
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#36A2EB',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }
    ]
  } : null;

  // Component bar chart data
  const componentChartData = data?.by_component ? {
    labels: data.by_component.map(c => c.component),
    datasets: [
      {
        label: 'Skip Count',
        data: data.by_component.map(c => c.skip_count),
        backgroundColor: chartColors.slice(0, data.by_component.length),
        borderWidth: 1,
      }
    ]
  } : null;

  // Reason pie chart data
  const reasonChartData = data?.by_reason ? {
    labels: data.by_reason.map(r => r.reason),
    datasets: [
      {
        data: data.by_reason.map(r => r.count),
        backgroundColor: chartColors.slice(0, data.by_reason.length),
        borderWidth: 1,
      }
    ]
  } : null;

  if (error) {
    return (
      <div className="analytics-tab skips-tab">
        <div className="error-message">
          <h4>Error Loading Skip Data</h4>
          <p>{error.message}</p>
          <button onClick={() => refetch()} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-tab skips-tab">
      {/* View Toggle */}
      <div className="view-toggle">
        <button
          className={`toggle-btn ${viewMode === 'overview' ? 'active' : ''}`}
          onClick={() => setViewMode('overview')}
        >
          Overview
        </button>
        <button
          className={`toggle-btn ${viewMode === 'components' ? 'active' : ''}`}
          onClick={() => setViewMode('components')}
        >
          Components
        </button>
        <button
          className={`toggle-btn ${viewMode === 'reasons' ? 'active' : ''}`}
          onClick={() => setViewMode('reasons')}
        >
          Reasons
        </button>
        <button
          className={`toggle-btn ${viewMode === 'words' ? 'active' : ''}`}
          onClick={() => setViewMode('words')}
        >
          Words
        </button>
        <button
          className={`toggle-btn ${viewMode === 'sections' ? 'active' : ''}`}
          onClick={() => setViewMode('sections')}
        >
          Sections
        </button>
        <button
          className={`toggle-btn ${viewMode === 'users' ? 'active' : ''}`}
          onClick={() => setViewMode('users')}
        >
          Users
        </button>
      </div>

      {/* Overview View */}
      {viewMode === 'overview' && (
        <div className="overview-content">
          {/* Summary Cards */}
          {loading ? (
            <div className="summary-cards">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="summary-card skeleton"></div>
              ))}
            </div>
          ) : data?.summary ? (
            <div className="summary-cards">
              <div className="summary-card skip-card">
                <span className="card-value">{data.summary.total_skip_events}</span>
                <span className="card-label">Total Skip Events</span>
              </div>
              <div className="summary-card skip-card">
                <span className="card-value">{data.summary.total_exercises_skipped}</span>
                <span className="card-label">Exercises Skipped</span>
              </div>
              <div className="summary-card skip-card">
                <span className="card-value">{data.summary.avg_exercises_per_skip}</span>
                <span className="card-label">Avg per Skip</span>
              </div>
              <div className="summary-card skip-card">
                <span className="card-value">{data.summary.unique_users_skipping}</span>
                <span className="card-label">Users Skipping</span>
              </div>
              <div className="summary-card skip-card">
                <span className="card-value">{data.summary.unique_components_skipped}</span>
                <span className="card-label">Components</span>
              </div>
              <div className="summary-card skip-card">
                <span className="card-value">{data.summary.unique_sections_affected}</span>
                <span className="card-label">Sections</span>
              </div>
            </div>
          ) : null}

          {/* Time Series Chart */}
          <div className="chart-container">
            <div className="chart-header">
              <h3>Skip Trends Over Time</h3>
            </div>
            {loading ? (
              <div className="chart-skeleton" style={{ height: '300px' }}></div>
            ) : timeSeriesChartData ? (
              <div className="chart-wrapper" style={{ height: '300px' }}>
                <Line
                  data={timeSeriesChartData}
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      legend: { display: true, position: 'top' as const }
                    }
                  }}
                />
              </div>
            ) : (
              <p className="no-data">No time series data available</p>
            )}
          </div>
        </div>
      )}

      {/* Components View */}
      {viewMode === 'components' && (
        <div className="components-content">
          <div className="chart-container">
            <div className="chart-header">
              <h3>Skips by Component</h3>
            </div>
            {loading ? (
              <div className="chart-skeleton" style={{ height: '300px' }}></div>
            ) : componentChartData ? (
              <div className="chart-wrapper" style={{ height: '300px' }}>
                <Bar
                  data={componentChartData}
                  options={{
                    ...chartOptions,
                    indexAxis: 'y' as const,
                  }}
                />
              </div>
            ) : (
              <p className="no-data">No component data available</p>
            )}
          </div>

          {/* Components Table */}
          <div className="table-container">
            <div className="table-header">
              <h3>Component Details</h3>
            </div>
            {loading ? (
              <div className="table-skeleton"></div>
            ) : data?.by_component && data.by_component.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Skip Count</th>
                    <th>Exercises Skipped</th>
                    <th>Unique Users</th>
                    <th>Avg/Skip</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_component.map((item, idx) => (
                    <tr key={idx}>
                      <td className="component-name">{item.component}</td>
                      <td>{item.skip_count}</td>
                      <td>{item.exercises_skipped}</td>
                      <td>{item.unique_users}</td>
                      <td>{item.avg_exercises_per_skip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-data">No component data available</p>
            )}
          </div>
        </div>
      )}

      {/* Reasons View */}
      {viewMode === 'reasons' && (
        <div className="reasons-content">
          <div className="reasons-row">
            <div className="chart-container pie-chart">
              <div className="chart-header">
                <h3>Skip Reasons Distribution</h3>
              </div>
              {loading ? (
                <div className="chart-skeleton" style={{ height: '300px' }}></div>
              ) : reasonChartData ? (
                <div className="chart-wrapper" style={{ height: '300px' }}>
                  <Pie
                    data={reasonChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { position: 'right' as const }
                      }
                    }}
                  />
                </div>
              ) : (
                <p className="no-data">No reason data available</p>
              )}
            </div>

            {/* Reasons Table */}
            <div className="table-container">
              <div className="table-header">
                <h3>Reason Details</h3>
              </div>
              {loading ? (
                <div className="table-skeleton"></div>
              ) : data?.by_reason && data.by_reason.length > 0 ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Reason</th>
                      <th>Count</th>
                      <th>Percentage</th>
                      <th>Exercises</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_reason.map((item, idx) => (
                      <tr key={idx}>
                        <td className="reason-name">{item.reason}</td>
                        <td>{item.count}</td>
                        <td>{item.percentage}%</td>
                        <td>{item.exercises_skipped}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="no-data">No reason data available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Words View */}
      {viewMode === 'words' && (
        <div className="words-content">
          <div className="table-container full-width">
            <div className="table-header">
              <h3>Most Skipped Words</h3>
            </div>
            {loading ? (
              <div className="table-skeleton"></div>
            ) : data?.by_word && data.by_word.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Word (Target)</th>
                    <th>Word (Base)</th>
                    <th>Word ID</th>
                    <th>Skip Count</th>
                    <th>Sections</th>
                    <th>Components</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_word.map((item, idx) => (
                    <tr key={idx}>
                      <td className="word-target">{item.word_target || '-'}</td>
                      <td className="word-base">{item.word_base || '-'}</td>
                      <td className="word-id">{item.word_id}</td>
                      <td>{item.skip_count}</td>
                      <td className="list-cell">
                        {item.sections.slice(0, 2).join(', ')}
                        {item.sections.length > 2 && ` +${item.sections.length - 2}`}
                      </td>
                      <td className="list-cell">
                        {item.components.slice(0, 2).join(', ')}
                        {item.components.length > 2 && ` +${item.components.length - 2}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-data">No word data available</p>
            )}
          </div>
        </div>
      )}

      {/* Sections View */}
      {viewMode === 'sections' && (
        <div className="sections-content">
          <div className="table-container full-width">
            <div className="table-header">
              <h3>Skips by Section</h3>
            </div>
            {loading ? (
              <div className="table-skeleton"></div>
            ) : data?.by_section && data.by_section.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Section</th>
                    <th>Skip Count</th>
                    <th>Exercises Skipped</th>
                    <th>Components Affected</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_section.map((item, idx) => (
                    <tr key={idx}>
                      <td className="section-name">{item.section}</td>
                      <td>{item.skip_count}</td>
                      <td>{item.exercises_skipped}</td>
                      <td>{item.unique_components}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-data">No section data available</p>
            )}
          </div>
        </div>
      )}

      {/* Users View */}
      {viewMode === 'users' && (
        <div className="users-content">
          <div className="table-container full-width">
            <div className="table-header">
              <h3>Users with Most Skips</h3>
            </div>
            {loading ? (
              <div className="table-skeleton"></div>
            ) : data?.by_user && data.by_user.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Skip Count</th>
                    <th>Exercises Skipped</th>
                    <th>Components</th>
                    <th>Sections</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_user.map((item, idx) => (
                    <tr key={idx}>
                      <td className="user-email">{item.user_id}</td>
                      <td>{item.skip_count}</td>
                      <td>{item.exercises_skipped}</td>
                      <td>{item.unique_components}</td>
                      <td>{item.unique_sections}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="no-data">No user data available</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
